import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UpsertCarePreferencesInput } from "@shared/validation/care.schema";
import { apiClient, ApiError } from "./client";

export interface CarePreferencesSummary {
  id: string;
  childId: string;
  behavioralNotes: string | null;
  calmingStrategies: string | null;
  comfortItems: string | null;
  communicationStyle: string | null;
  createdAt: string;
  updatedAt: string;
}

const careKey = (childId: string) => ["children", childId, "care"] as const;

export function useCarePreferences(childId: string | undefined) {
  return useQuery<CarePreferencesSummary | null, ApiError>({
    queryKey: childId ? careKey(childId) : ["children", "__none__", "care"],
    queryFn: () => {
      // enabled: false when childId is undefined, so this only runs with a
      // real id — but narrow explicitly for TS.
      if (!childId) throw new Error("useCarePreferences: childId is required");
      return apiClient<CarePreferencesSummary | null>(`/api/care/${childId}`);
    },
    enabled: Boolean(childId),
  });
}

export function useSaveCarePreferences(childId: string) {
  const queryClient = useQueryClient();
  return useMutation<CarePreferencesSummary, ApiError, UpsertCarePreferencesInput>({
    mutationFn: (vars) =>
      apiClient<CarePreferencesSummary>(`/api/care/${childId}`, {
        method: "PUT",
        body: JSON.stringify(vars),
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData(careKey(childId), saved);
    },
  });
}
